/**
 * AdvisorQueueContext — ONE shared Advisor Action Queue for the whole app.
 *
 * The AI's "N to approve" badge (in the always-mounted AIControlBar) and the
 * Suggested-Actions inbox (in the AICockpit page) used to each call
 * useAdvisorQueue() independently. That hook keeps its own local `pending`
 * array and derives `count` from it, so the two instances drifted: the badge
 * accumulated `advisor.proposed` pushes it never reconciled (TTL-expired
 * proposals sent no push, so it climbed forever) while the freshly-mounted
 * inbox re-queried and showed something else entirely.
 *
 * Hoisting the queue into a single provider makes that impossible — every
 * surface reads the same `pending`/`count`, so they cannot disagree.
 */
import React, { createContext, useContext } from 'react';
import { useAdvisorQueueState } from '../hooks/useAdvisorQueue';

const AdvisorQueueContext = createContext(null);

// Stable no-op value for consumers rendered outside the provider (e.g. isolated
// tests). Keeps the UI from crashing; real data only flows under the provider.
const EMPTY = Object.freeze({
    pending: [],
    count: 0,
    busyIds: {},
    feedback: null,
    droppedByCap: 0,
    approve: () => {},
    dismiss: () => {},
    refresh: () => {},
    clearFeedback: () => {},
});

export function AdvisorQueueProvider({ children }) {
    const value = useAdvisorQueueState();
    return (
        <AdvisorQueueContext.Provider value={value}>
            {children}
        </AdvisorQueueContext.Provider>
    );
}

/**
 * Read the shared Advisor queue. Same object for every consumer, so the badge
 * count and the inbox list are guaranteed to agree.
 *
 * @returns {{ pending, count, approve, dismiss, refresh, busyIds, feedback, clearFeedback, droppedByCap }}
 */
export function useAdvisorQueue() {
    return useContext(AdvisorQueueContext) || EMPTY;
}

export { AdvisorQueueContext };
